import type { MedalTallyRow } from "@seagames/contracts";
import { useFilterDraft } from "@seagames/ui";
import { keepPreviousData, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { DASHBOARD_POLL_MS } from "../../components/dashboard/queries";
import { MedalList } from "../../components/medals/medal-list";
import { MedalsFilters } from "../../components/medals/medals-filters";
import { MEDAL_LIST_PAGE, medalListQuery, tallyQuery } from "../../components/medals/queries";
import {
	EMPTY_MEDALS_DRAFT,
	type MedalsDraft,
	type MedalsSearch,
	medalListFilters,
	medalListScope,
	medalsDraftFromSearch,
	medalsFilterCount,
	medalsSearch,
	medalsSearchFromDraft,
} from "../../components/medals/search";
import { TallyTable } from "../../components/medals/tally-table";
import { PageHeader } from "../../components/page/page-header";
import { Button } from "../../components/ui/button";
import { EmptyState } from "../../components/ui/empty-state";
import { Spinner } from "../../components/ui/spinner";
import { errorMessage } from "../../lib/api/messages";
import { gamesSportsQuery, useCurrentGames } from "../../lib/games";
import { formatInteger } from "../../lib/i18n/format";
import { nocName } from "../../components/nocs/noc-name";

/**
 * Medal tally — the current games' medals per NOC, as the API ranks them
 * (gold, then silver, then bronze), beside the awarded medals newest first.
 * Clicking a NOC narrows the list to its medals (`?noc=`); the Sport filter
 * (`?sport=`) narrows both — the tally becomes that sport's table (still one
 * `GET /medals/tally`, with `sport_id`). Read-only for every OVR role: the
 * medal desk (award, lock) is not part of OVR.
 */
export const Route = createFileRoute("/_app/medals")({
	validateSearch: medalsSearch,
	component: MedalsPage,
});

function MedalsPage() {
	const { t } = useTranslation("medals");
	const search = Route.useSearch();
	const navigate = useNavigate({ from: "/medals" });
	const current = useCurrentGames();
	const gamesId = current.gamesId;

	const tally = useQuery({
		...tallyQuery(gamesId ?? "", search.sport),
		enabled: gamesId !== null,
		refetchInterval: DASHBOARD_POLL_MS * 2,
		placeholderData: keepPreviousData,
	});
	// The games-wide tally, only if it is already cached (never fetched here): with a sport picked, the
	// sport's table lists only the NOCs entered in it, and the NOC picker and headings still need the rest.
	const gamesTally = useQuery({ ...tallyQuery(gamesId ?? ""), enabled: false });
	const sports = useQuery({ ...gamesSportsQuery(gamesId ?? ""), enabled: gamesId !== null });
	const filters = gamesId ? medalListFilters(gamesId, search) : null;
	const list = useInfiniteQuery({ ...medalListQuery(filters ?? { games_id: "" }), enabled: filters !== null, placeholderData: keepPreviousData });
	const medals = useMemo(() => list.data?.pages.flatMap((p) => p.items) ?? [], [list.data]);

	const go = useCallback((next: MedalsSearch) => void navigate({ search: next }), [navigate]);
	const applied = useMemo(() => medalsDraftFromSearch(search), [search]);
	const f = useFilterDraft<MedalsDraft>(applied, (d) => go(medalsSearchFromDraft(d)), EMPTY_MEDALS_DRAFT);

	// The sidebar switched games: a sport or NOC of the old games means nothing in the new one.
	const lastGames = useRef(gamesId);
	useEffect(() => {
		const previous = lastGames.current;
		lastGames.current = gamesId;
		if (previous !== null && gamesId !== null && previous !== gamesId) void navigate({ search: {}, replace: true });
	}, [gamesId, navigate]);

	// Opening a NOC: its medals are listed under the tally — bring the list up when it starts below the fold.
	const listRef = useRef<HTMLDivElement>(null);
	const scrollOnOpen = useRef(false);
	useEffect(() => {
		if (!scrollOnOpen.current) return;
		scrollOnOpen.current = false;
		const el = listRef.current;
		if (!el || el.getBoundingClientRect().top < window.innerHeight * 0.6) return;
		const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
		el.scrollIntoView({ block: "start", behavior: reduced ? "auto" : "smooth" });
	}, [search.noc]);
	const openNoc = (row: MedalTallyRow) => {
		scrollOnOpen.current = true;
		// Keep the place on the page (the router would jump to the top); the effect above brings the list up.
		void navigate({ search: { ...search, noc: row.noc.responsible_organization_id }, resetScroll: false });
	};

	if (current.error) {
		return (
			<div className="flex flex-col">
				<PageHeader title={t("title")} />
				<EmptyState title={t("errors.gamesTitle")} body={errorMessage(current.error)} />
			</div>
		);
	}
	if (current.isLoading) return <Spinner className="size-[1.5rem] text-board" />;
	if (!current.games) {
		return (
			<div className="flex flex-col">
				<PageHeader title={t("title")} />
				<EmptyState title={t("empty.noGamesTitle")} body={t("empty.noGamesBody")} />
			</div>
		);
	}

	const rows = tally.data?.rows ?? [];
	const allRows = search.sport ? (gamesTally.data?.rows ?? rows) : rows;
	const nocRow = rows.find((r) => r.noc.responsible_organization_id === search.noc);
	const noc =
		nocRow?.noc ??
		allRows.find((r) => r.noc.responsible_organization_id === search.noc)?.noc ??
		medals.find((m) => m.competitor.noc.responsible_organization_id === search.noc)?.competitor.noc;
	const nocLabel = noc ? (noc.code ?? nocName(noc)) : "";
	const sportLabel = sports.data?.find((s) => s.sport_id === search.sport)?.name ?? "";
	const scope = medalListScope(search);
	const listTitle =
		scope === "nocSport" && nocLabel
			? t("list.ofNocInSport", { noc: nocLabel, sport: sportLabel })
			: scope === "noc"
				? t("list.ofNoc", { noc: nocLabel })
				: scope === "sport" || scope === "nocSport"
					? t("list.inSport", { sport: sportLabel })
					: t("list.latest");
	const awardedAny = rows.some((r) => r.total > 0);
	const filtered = medalsFilterCount(search) > 0;

	return (
		<div className="flex flex-col">
			<PageHeader title={t("title")} subtitle={t("subtitle", { games: current.games.name })} />

			<div className="flex flex-col gap-[1rem]">
				<MedalsFilters
					draft={f.draft}
					set={f.set}
					onApply={f.apply}
					onReset={f.reset}
					activeCount={medalsFilterCount(search)}
					sports={sports.data ?? []}
					nocs={allRows}
				/>

				{tally.isSuccess && rows.length > 0 && !awardedAny && (
					<p role="status" className="rounded-card border border-rule bg-sheet px-[1rem] py-[0.75rem] text-[0.9375rem] text-muted">
						{search.sport ? t("tally.noneAwardedInSport", { sport: sportLabel }) : t("tally.noneAwarded")}
					</p>
				)}

				{/* Stacked, not side by side: the tally and the list each need the full width at 1440 with the sidebar open. */}
				<div className="flex flex-col gap-[1rem]">
					<TallyTable
						rows={rows}
						selected={search.noc}
						loading={tally.isPending || (tally.isFetching && tally.isPlaceholderData)}
						error={tally.isError ? { message: errorMessage(tally.error), onRetry: () => void tally.refetch() } : null}
						title={search.sport && sportLabel ? t("tally.titleInSport", { sport: sportLabel }) : t("tally.title")}
						onOpen={openNoc}
					/>

					<div ref={listRef} className="flex scroll-mt-[4.5rem] flex-col gap-[0.75rem]">
						<MedalList
							title={listTitle}
							description={noc && noc.code ? nocName(noc) : undefined}
							actions={
								search.noc ? (
									<Button variant="outline" size="sm" onClick={() => void navigate({ search: { ...search, noc: undefined }, resetScroll: false })}>
										{t("list.clearNoc")}
									</Button>
								) : null
							}
							rows={medals}
							loading={list.isPending || (list.isFetching && !list.isFetchingNextPage && list.isPlaceholderData)}
							error={list.isError ? { message: errorMessage(list.error), onRetry: () => void list.refetch() } : null}
							empty={
								filtered
									? {
											title: t("list.emptyFilteredTitle"),
											description: t("list.emptyFilteredBody"),
											action: (
												<Button variant="outline" size="sm" onClick={f.reset}>
													{t("list.reset")}
												</Button>
											),
										}
									: { title: t("list.emptyTitle"), description: t("list.emptyBody") }
							}
							toolbar={
								medals.length > 0 ? (
									<span className="text-[0.8125rem] text-muted">
										{/* The tally row is the same scope as the list (all sports, or the one sport): it knows the total. */}
										{nocRow
											? t("list.showingOf", { count: nocRow.total, n: formatInteger(medals.length), total: formatInteger(nocRow.total) })
											: t("list.showing", { count: medals.length, n: formatInteger(medals.length) })}
									</span>
								) : null
							}
						/>
						{medals.length > 0 && (
							<div className="flex flex-wrap items-center justify-center gap-[0.75rem]">
								{list.hasNextPage ? (
									<Button variant="outline" loading={list.isFetchingNextPage} onClick={() => void list.fetchNextPage()}>
										{t("list.more", { n: formatInteger(MEDAL_LIST_PAGE) })}
									</Button>
								) : (
									<p className="text-[0.875rem] text-muted">{t("list.allLoaded")}</p>
								)}
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}
